// ABOUTME: Feedback service for user feedback submission and management
// ABOUTME: Handles PocketBase integration, email notifications, and feedback history
import pb from './pocketbaseClient';
import ErrorHandler from './errorHandler';
import { safeFilter } from '../utils/pbFilterEscape'; // TD3-191: SQL injection prevention

class FeedbackService {
  /**
   * Submit user feedback to PocketBase
   * @param {string} userId - User ID from auth
   * @param {string} userEmail - User email for admin convenience
   * @param {string} feedbackText - The feedback content
   * @param {string} feedbackType - Type: bug, suggestion, praise, other
   * @param {string} page - Page where feedback was submitted
   * @param {string} userName - User's nickname for email greeting (optional)
   * @returns {Promise<{success: boolean, feedbackId?: string, error?: string}>}
   */
  async submitFeedback(userId, userEmail, feedbackText, feedbackType, page, userName = null) {
    try {
      // Validate inputs
      if (!userId || !userEmail || !feedbackText || !feedbackType || !page) {
        return {
          success: false,
          error: 'Todos os campos são obrigatórios'
        };
      }

      // TD3-105: Minimum 2 chars (UX-friendly, only validates on submit)
      if (feedbackText.length < 2) {
        return {
          success: false,
          error: 'Feedback deve ter pelo menos 2 caracteres'
        };
      }

      if (feedbackText.length > 5000) {
        return {
          success: false,
          error: 'Feedback não pode exceder 5000 caracteres'
        };
      }

      const validTypes = ['bug', 'suggestion', 'praise', 'other'];
      if (!validTypes.includes(feedbackType)) {
        return {
          success: false,
          error: 'Tipo de feedback inválido'
        };
      }

      console.log(`📝 Submitting feedback from user ${userEmail} on page ${page}`);

      // Create feedback record in PocketBase
      const record = await pb.collection('user_feedback').create({
        userId: userId,
        userEmail: userEmail,
        feedbackText: feedbackText.trim(),
        feedbackType: feedbackType,
        page: page,
        status: 'new'
      });

      console.log(`✅ Feedback submitted successfully: ${record.id}`);

      // Send email notifications via VPS endpoint
      // TD3-206: Pass userName (nickname) instead of userEmail for greeting
      try {
        await this.sendEmailNotifications(record, userName);
      } catch (emailError) {
        // Don't fail the feedback submission if email fails
        console.warn('Email notification failed, but feedback was saved:', emailError);
      }

      return {
        success: true,
        feedbackId: record.id
      };
    } catch (error) {
      console.error('Feedback submission error:', error);

      // Capture error for monitoring
      await ErrorHandler.captureError(
        ErrorHandler.ERROR_TYPES.DATABASE,
        `Feedback submission failed: ${error.message}`,
        userId,
        { feedbackType, page, errorStack: error.stack }
      );

      return {
        success: false,
        error: error.message || 'Erro ao enviar feedback. Tente novamente.'
      };
    }
  }

  /**
   * Send email notifications via VPS endpoint
   * @private
   * @param {object} feedbackRecord - The feedback record from PocketBase
   * @param {string} userName - User's nickname for email greeting
   */
  async sendEmailNotifications(feedbackRecord, userName) {
    // TD3-105: HTTPS endpoint required for production (Mixed Content Policy)
    const emailEndpoint = process.env.REACT_APP_EMAIL_ENDPOINT || 'https://oraculo.buenaar.app/claude/api/send-feedback-email';

    try {
      // TD3-206: Use nickname for email greeting, fallback to email username part
      const displayName = userName || feedbackRecord.userEmail.split('@')[0];

      const response = await fetch(emailEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userEmail: feedbackRecord.userEmail,
          userName: displayName,
          feedbackType: this.getFeedbackTypeDisplayName(feedbackRecord.feedbackType),
          feedbackText: feedbackRecord.feedbackText,
          page: feedbackRecord.page,
          feedbackId: feedbackRecord.id
        })
      });

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.error || 'Email sending failed');
      }

      console.log('✅ Email notifications sent successfully');
    } catch (error) {
      console.error('Email notification error:', error);
      // Don't throw - we don't want to fail feedback submission if email fails
    }
  }

  /**
   * Get user's feedback history
   * @param {string} userId - User ID from auth
   * @returns {Promise<{success: boolean, feedbacks?: Array, error?: string}>}
   */
  async getUserFeedbacks(userId) {
    try {
      if (!userId) {
        return {
          success: false,
          error: 'Usuário não autenticado'
        };
      }

      console.log(`📋 Loading feedback history for user: ${userId}`);

      // TD3-191: Use safeFilter to prevent SQL injection
      const records = await pb.collection('user_feedback').getFullList({
        filter: safeFilter`userId = "${userId}"`,
        sort: '-created',
        fields: 'id,feedbackText,feedbackType,page,status,created'
      });

      console.log(`✅ Loaded ${records.length} feedback entries`);

      return {
        success: true,
        feedbacks: records
      };
    } catch (error) {
      console.error('Error loading feedback history:', error);

      await ErrorHandler.captureError(
        ErrorHandler.ERROR_TYPES.DATABASE,
        `Failed to load feedback history: ${error.message}`,
        userId,
        { errorStack: error.stack }
      );

      return {
        success: false,
        error: 'Erro ao carregar histórico de feedback'
      };
    }
  }

  /**
   * TD3-105: Translate page identifier to Portuguese for user-facing display
   * IMPORTANT: This is ONLY for user-facing UI (Settings history)
   * Admin emails receive English page names for clarity
   * @param {string} page - Page identifier (e.g., "dashboard", "oracle-step-6")
   * @returns {string} Portuguese display name
   */
  getPageDisplayName(page) {
    // TD3-105: Portuguese translations for user-facing history
    // Admin emails keep English page names for technical clarity
    const pageTranslations = {
      'dashboard': 'Painel',
      'settings': 'Configurações',
      'oracle-step-6': 'Oráculo - Seleção de Raio',
      'oracle-step-7': 'Oráculo - Pergunta',
      'oracle-step-8': 'Oráculo - Diagnóstico (Tipo)',
      'oracle-step-9': 'Oráculo - Diagnóstico (Elemento)',
      'oracle-step-10': 'Oráculo - Solução (Tipo)',
      'oracle-step-11': 'Oráculo - Solução (Elemento)',
      'oracle-step-12': 'Oráculo - Bloqueio',
      'oracle-step-13': 'Oráculo - Solução',
      'oracle-step-14': 'Oráculo - Sabedoria',
      'oracle-step-15': 'Oráculo - Decreto',
      'oracle-step-16': 'Oráculo - Revisão Final'
    };
    return pageTranslations[page] || page;
  }

  /**
   * Get feedback type display name in Portuguese
   * @param {string} type - Feedback type
   * @returns {string} Display name
   */
  getFeedbackTypeDisplayName(type) {
    const types = {
      bug: 'Bug/Erro',
      suggestion: 'Sugestão',
      praise: 'Elogio',
      other: 'Outro'
    };
    return types[type] || type;
  }

  /**
   * Get feedback status display name in Portuguese
   * @param {string} status - Feedback status
   * @returns {string} Display name
   */
  getFeedbackStatusDisplayName(status) {
    const statuses = {
      new: 'Novo',
      reviewed: 'Em Análise',
      resolved: 'Resolvido',
      'wont-fix': 'Não Será Implementado'
    };
    return statuses[status] || status;
  }

  /**
   * Get feedback status color for UI
   * @param {string} status - Feedback status
   * @returns {string} CSS color class
   */
  getFeedbackStatusColor(status) {
    const colors = {
      new: '#3B82F6', // blue
      reviewed: '#F59E0B', // amber
      resolved: '#10B981', // green
      'wont-fix': '#6B7280' // gray
    };
    return colors[status] || colors.new;
  }
}

// Export singleton instance
const feedbackService = new FeedbackService();
export default feedbackService;
